/**
 * View model for the Agents right-panel surface.
 *
 * Forge's delegated work is worker *threads* — each with a branch, a review
 * lifecycle, and its own transcript — not the ephemeral in-thread subagents the
 * surface this is modelled on tracks. So the data is Forge's; the presentation
 * rules are borrowed, because they were tuned against a live fleet:
 *
 * - **Spawn order is stable.** Rows never reorder on activity. The compact
 *   WorkBar row sorts attention-first because only a few rows fit there and the
 *   urgent one has to be among them; a panel is for watching, and rows that
 *   move while you read them are worse than rows you have to scan.
 * - **In-flight is one steady state.** Every not-yet-settled status reads as
 *   "Working". Which flavour of in-flight a worker is in belongs in the detail
 *   line, not in a colour the user has to decode. Only settled states earn a
 *   distinct dot.
 * - **Rows are fixed height.** Three lines — identity, detail, metrics — always,
 *   so arriving data never reflows the list under the pointer.
 *
 * Pure and mount-free so the mapping is unit-testable.
 */

import type { ThreadStatus, WorkerRole } from "@cmmd-forge/contracts";

/** Dot tone. Kept symbolic so the component owns the class, not this module. */
export type AgentStatusTone = "working" | "attention" | "settled-ok" | "settled-bad" | "resting";

export interface AgentStatusVisual {
  readonly tone: AgentStatusTone;
  readonly label: string;
}

/**
 * Exhaustive by construction: `Record<ThreadStatus, …>` fails typecheck the
 * moment a status is added to the contract, which is the only reliable way to
 * keep a new lifecycle state from silently rendering as blank.
 */
const STATUS_VISUALS: Record<ThreadStatus, AgentStatusVisual> = {
  running: { tone: "working", label: "Working" },
  ready: { tone: "working", label: "Working" },
  // Resting, not working: an idle worker is waiting on a person, and colouring
  // it like live work makes the fleet look busier than it is.
  idle: { tone: "resting", label: "Idle" },
  stuck: { tone: "settled-bad", label: "Stuck" },
  conflicted: { tone: "settled-bad", label: "Conflicted" },
  // Attention, not success: the header counts this row as needing a person, and
  // a green dot beside that count would contradict it.
  "needs-review": { tone: "attention", label: "Needs review" },
  merged: { tone: "settled-ok", label: "Merged" },
  archived: { tone: "resting", label: "Archived" },
};

export function agentStatusVisual(status: ThreadStatus): AgentStatusVisual {
  return STATUS_VISUALS[status];
}

/** Statuses that still count as live work for the header and the bar badge. */
const ACTIVE_STATUSES: ReadonlySet<ThreadStatus> = new Set<ThreadStatus>([
  "idle",
  "running",
  "ready",
  "stuck",
  "needs-review",
  "conflicted",
]);

/** Statuses a person has to act on. Drives the attention count, not the order. */
const ATTENTION_STATUSES: ReadonlySet<ThreadStatus> = new Set<ThreadStatus>([
  "stuck",
  "needs-review",
  "conflicted",
]);

export interface AgentsPanelWorkerInput {
  readonly id: string;
  readonly title: string;
  readonly status: ThreadStatus;
  readonly branch: string | null;
  readonly modelLabel: string | null;
  readonly createdAt: string;
  readonly updatedAt?: string | undefined;
  readonly workerRole?: WorkerRole | null;
}

export interface AgentRowViewModel {
  readonly id: string;
  readonly title: string;
  readonly status: ThreadStatus;
  readonly visual: AgentStatusVisual;
  readonly branch: string | null;
  readonly modelLabel: string | null;
  readonly startedAt: string;
  /**
   * When the row's clock should stop. Settled workers freeze at their last
   * update; live ones tick against now. Null means "still running".
   */
  readonly endedAt: string | null;
  readonly isCurrent: boolean;
  readonly workerRole: WorkerRole | null;
}

export interface AgentsPanelViewModel {
  readonly rows: readonly AgentRowViewModel[];
  readonly activeCount: number;
  readonly attentionCount: number;
}

export interface BuildAgentsPanelInput {
  readonly workers: readonly AgentsPanelWorkerInput[];
  readonly currentWorkerId?: string | undefined;
}

/** A worker whose clock has stopped: nothing more will happen without a person. */
function isSettled(status: ThreadStatus): boolean {
  return status === "merged" || status === "archived";
}

export function buildAgentsPanelViewModel(input: BuildAgentsPanelInput): AgentsPanelViewModel {
  // Spawn order, resolved from `createdAt` rather than trusting array order,
  // which varies with how the store merged its last update. Ties fall back to
  // id so the sort is total and two workers created in the same millisecond
  // cannot swap places between renders.
  const ordered = input.workers.toSorted((left, right) => {
    const byCreated = Date.parse(left.createdAt) - Date.parse(right.createdAt);
    if (Number.isFinite(byCreated) && byCreated !== 0) {
      return byCreated;
    }
    return left.id.localeCompare(right.id);
  });

  const rows = ordered.map((worker): AgentRowViewModel => ({
    id: worker.id,
    title: worker.title,
    status: worker.status,
    visual: agentStatusVisual(worker.status),
    branch: worker.branch,
    modelLabel: worker.modelLabel,
    startedAt: worker.createdAt,
    endedAt: isSettled(worker.status) ? (worker.updatedAt ?? null) : null,
    isCurrent: input.currentWorkerId !== undefined && worker.id === input.currentWorkerId,
    workerRole: worker.workerRole ?? null,
  }));

  return {
    rows,
    activeCount: rows.filter((row) => ACTIVE_STATUSES.has(row.status)).length,
    attentionCount: rows.filter((row) => ATTENTION_STATUSES.has(row.status)).length,
  };
}

/**
 * `1m 04s` / `2h 09m` / `43s`.
 *
 * Zero-padded on the trailing unit so the string keeps a stable width as it
 * counts — the metrics line is written straight to the DOM on a timer, and a
 * value that changes width would jitter the row it sits in.
 */
export function formatAgentElapsed(totalSeconds: number): string {
  const seconds = Math.max(0, Math.floor(totalSeconds));
  const minutes = Math.floor(seconds / 60);
  if (minutes === 0) {
    return `${seconds}s`;
  }
  const hours = Math.floor(minutes / 60);
  if (hours === 0) {
    return `${minutes}m ${String(seconds % 60).padStart(2, "0")}s`;
  }
  return `${hours}h ${String(minutes % 60).padStart(2, "0")}m`;
}

/** Elapsed between two ISO stamps, or "" when either is unparseable. */
export function agentElapsedLabel(
  startedAt: string,
  endedAt: string | null,
  nowMs: number,
): string {
  const start = Date.parse(startedAt);
  const end = endedAt === null ? nowMs : Date.parse(endedAt);
  if (Number.isNaN(start) || Number.isNaN(end)) {
    return "";
  }
  return formatAgentElapsed((end - start) / 1000);
}
